import { useAuth } from "@clerk/clerk-react";
import { Grid, TextField, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import lodash from "lodash";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BentoSearchFilter } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import StyledCard from "components/Styled/Card";
import StyledVaPage from "components/Styled/VAPage";
import { getUserType } from "features/Auth/Onboarding/AboutForm/helpers";
import OnboardingSettings from "features/Influencer/VA/VASettings/GeneralSettings/OnboardingSettings";
import { fetcherAuth } from "utils/api";
import { getSource } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useTracking } from "utils/useTracking";

import styles from "./styles";

interface Props {
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  nichesFetched: boolean;
}

const SelfServeAboutPage = ({
  onboardingForm,
  setOnboardingForm,
  nichesFetched,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { trackVaStep } = useTracking();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const {
    currentOrg,
    includedBentoSearchFilterIds,
    excludedBentoSearchFilterIds,
    setIncludedBentoSearchFilterIds,
    setExcludedBentoSearchFilterIds,
  } = useContext(OrganizationUserContext);

  const [vaSearchFilters, setVaSearchFilters] = useState<BentoSearchFilter[]>(
    [],
  );
  const [
    localIncludedBentoSearchFilterIds,
    setLocalIncludedBentoSearchFilterIds,
  ] = useState<number[]>(includedBentoSearchFilterIds);
  const [
    localExcludedBentoSearchFilterIds,
    setLocalExcludedBentoSearchFilterIds,
  ] = useState<number[]>(excludedBentoSearchFilterIds);
  const [isEdited, setIsEdited] = useState<boolean>(false);

  const fetchFilters = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-search-filters?for_va=true`,
        "GET",
      );
      setVaSearchFilters(res.bentoSearchFilters);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleValidateForm = () => {
    if (
      !onboardingForm?.addressComponents ||
      onboardingForm?.addressComponents.length < 2
    ) {
      setAlert("Please fill in a city or zip code", "warning");
      return false;
    }

    if (!onboardingForm?.city) {
      const neighborhood = onboardingForm.addressComponents.find(
        (component: any) =>
          component.types.includes("sublocality") ||
          component.types.includes("neighborhood"),
      )?.longText;
      if (neighborhood) {
        onboardingForm.city = neighborhood;
      } else {
        onboardingForm.city = onboardingForm.addressComponents[0].longText;
      }
    }

    if (!onboardingForm?.location) {
      setAlert(
        "We could not extract the country from your location, please input the full address like `Kosovo Polje, Bosnia and Herzegovina`",
        "warning",
      );
      return false;
    }

    if (getUserType(onboardingForm) === "") {
      setAlert("Please answer how you plan to use Bento.", "warning");
      return false;
    }

    return true;
  };

  const handleChange = (e: CustomEvent) => {
    setIsEdited(true);
    const { name, value } = e.target;
    const copy = makeDeepCopy(onboardingForm);
    copy[name] = value;
    setOnboardingForm(copy);
  };

  const handleLocationChange = (userLocation: UserLocation) => {
    setIsEdited(true);
    setOnboardingForm((prev) => ({
      ...prev,
      city: userLocation.address,
      location: userLocation.country_code,
      addressComponents: userLocation.address_components,
      longitude: userLocation.longitude,
      latitude: userLocation.latitude,
    }));
  };

  const handleSubmitAbout = async () => {
    const isValid = handleValidateForm();
    if (!isValid) return;

    if (!isEdited) {
      navigate(`/${routes.nicheSelfServeOnboarding}`);
      return;
    }

    trackVaStep(routes.nicheSelfServeOnboarding);
    navigate(`/${routes.nicheSelfServeOnboarding}`);

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        {
          ...onboardingForm,
          page: location.pathname,
          source: getSource(),
          isVaFlow: true,
        },
      );
      await updateFilters();
    } catch (error) {
      setErrorAlert(error);
      navigate(`/${routes.aboutSelfServeOnboarding}`);
    }
  };

  const updateFilters = async () => {
    const body: Map = {};
    if (
      !lodash.isEqual(
        localExcludedBentoSearchFilterIds,
        excludedBentoSearchFilterIds,
      )
    ) {
      body.excludeBentoSearchFilterIds = localExcludedBentoSearchFilterIds;
    }
    if (
      !lodash.isEqual(
        localIncludedBentoSearchFilterIds,
        includedBentoSearchFilterIds,
      )
    ) {
      body.includeBentoSearchFilterIds = localIncludedBentoSearchFilterIds;
    }
    if (body.excludeBentoSearchFilterIds || body.includeBentoSearchFilterIds) {
      try {
        await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/va-settings`,
          "PUT",
          {},
          {
            ...body,
          },
        );
        setIncludedBentoSearchFilterIds(localIncludedBentoSearchFilterIds);
        setExcludedBentoSearchFilterIds(localExcludedBentoSearchFilterIds);
      } catch (error) {
        setErrorAlert(error);
        navigate(`/${routes.aboutSelfServeOnboarding}`);
      }
    }
  };

  useEffect(() => {
    if (currentOrg) {
      fetchFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <StyledVaPage
      stepNumber={1}
      title="Let's Get to Know You ✍️"
      description=""
      isInsideForm={true}
      body={
        <Grid container justifyContent="center">
          <StyledCard styles={{ px: 2, py: 4, mb: 4, maxWidth: 500 }}>
            <Grid item xs={12} sx={styles.fieldContainer}>
              <Typography gutterBottom>Your Name *</Typography>
              <TextField
                sx={styles.textField}
                fullWidth
                name="name"
                placeholder="First Last"
                onChange={handleChange}
                required
                value={onboardingForm?.name || ""}
                InputProps={{
                  sx: { borderRadius: 4 },
                }}
              />
            </Grid>
            <Grid item xs={12} sx={styles.fieldContainer}>
              <Typography gutterBottom>City or Zip Code *</Typography>
              <LocationAutocomplete
                handleLocationChange={handleLocationChange}
                userLocation={{
                  address: onboardingForm.city,
                  longitude: onboardingForm.longitude,
                  latitude: onboardingForm.latitude,
                  country_code: onboardingForm.location,
                  address_components: onboardingForm.addressComponents,
                }}
                customStyling={{
                  height: 52,
                  width: "100%",
                  borderRadius: 4,
                  border: `1px solid ${grey[300]}`,
                  px: 1,

                  "&:hover, &:focus": {
                    outline: "none",
                  },
                }}
              />
            </Grid>
            <OnboardingSettings
              vaSearchFilters={vaSearchFilters}
              // @ts-ignore
              profile={onboardingForm}
              // @ts-ignore
              setProfile={setOnboardingForm}
              includedBentoSearchFilterIds={localIncludedBentoSearchFilterIds}
              excludedBentoSearchFilterIds={localExcludedBentoSearchFilterIds}
              setIncludedBentoSearchFilterIds={
                setLocalIncludedBentoSearchFilterIds
              }
              setIsEdited={setIsEdited}
              setExcludedBentoSearchFilterIds={
                setLocalExcludedBentoSearchFilterIds
              }
            />
          </StyledCard>
        </Grid>
      }
      buttonNext={"Continue to Niches"}
      onNext={handleSubmitAbout}
      buttonBack="Go Back to Program Information"
      onBack={() => {
        trackVaStep(routes.welcomeSelfServeOnboarding);
        navigate(`/${routes.welcomeSelfServeOnboarding}`);
      }}
      loading={!nichesFetched}
    />
  );
};

export default SelfServeAboutPage;
